"use client";

import React from "react";
import { Check, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const plans = [{
  id: "standard",
  name: "CAD Design",
  duration: "Starting",
  price: "$50",
  description: "Professional 3D modeling for custom jewelry pieces.",
  benefits: ["High-Resolution Renderings", "Industry Standard .3dm Files", "Print-Ready .stl Exports", "Detailed .jpg Previews"],
  isPopular: false,
  buttonText: "Request Quote",
  link: "/StartYourProjectPage"
}, {
  id: "gold",
  name: "3D Printing",
  duration: "/ STARTING / PER PIECE",
  price: "$15",
  description: "Precision wax samples for casting and production.",
  benefits: ["7 Piece Minimum Order", "High-Detail Wax Resins", "Rapid Prototyping Speed", "Ready for Investment Casting", "Quality Control Inspection"],
  isPopular: true,
  buttonText: "Start Printing",
  link: "/StartYourProjectPage"
}, {
  id: "silver",
  name: "Detailed Models",
  duration: "Custom",
  price: "$200",
  description: "Intricate designs with complex stone settings.",
  benefits: ["Highly Detailed Geometry", "Advanced Pave Settings", "Multiple Revision Rounds", "Full Technical Support", "Production-Ready Files"],
  isPopular: false,
  buttonText: "Discuss Project",
  link: "/StartYourProjectPage"
}];
export default function NosForfaitsPremium() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224297" id="service-tiers" ref={sectionRef} className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-lg opacity-50" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-secondary/5 blur-[100px] rounded-lg opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-default text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            Transparent <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Investment</span>
          </h2>
          <p className="text-muted-foreground text-lg font-default">
            Precision-engineered jewelry services tailored to your production needs. From digital concepts to physical wax prototypes.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-center max-w-6xl mx-auto">
          {plans.map((plan, i) => <div data-index={i} key={plan.id} className={`transition-all duration-700 delay-[${i * 150}ms] transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${plan.isPopular ? "md:order-2" : i === 0 ? "md:order-1" : "md:order-3"}`}>
              <Card className={`relative h-full flex flex-col bg-gradient-to-b from-card to-background/40 backdrop-blur-xl border-border/30 transition-all duration-300 hover:shadow-lg rounded-md ${plan.isPopular ? "md:scale-110 z-20 border-2 border-secondary shadow-xl" : "z-10 hover:border-primary/50 shadow-md"}`}>
                {plan.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center z-30">
                    <Badge className="bg-secondary text-secondary-foreground px-4 py-1 text-sm font-bold uppercase tracking-wider shadow-md flex items-center gap-1 rounded-md">
                      <Sparkles className="w-4 h-4" />
                      7 Piece Minimum
                    </Badge>
                  </div>}

                <CardHeader className="text-center pb-2 pt-8">
                  <CardTitle className="font-default text-2xl text-primary mb-2">
                    {plan.name}
                  </CardTitle>
                  <div className="flex justify-center items-baseline gap-1 mb-2">
                    <span className="font-mono text-5xl font-bold text-foreground drop-shadow-sm">
                      {plan.price}
                    </span>
                    <span className="text-muted-foreground text-sm font-mono uppercase tracking-widest">
                      / {plan.duration}
                    </span>
                  </div>
                  <CardDescription className="text-sm font-default min-h-[40px] text-muted-foreground">
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow pt-6">
                  <ul className="space-y-4">
                    {plan.benefits.map((benefit, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-sm font-default">
                        <div className="mt-0.5 bg-secondary/10 p-1 rounded-md shrink-0">
                          <Check className="w-4 h-4 text-secondary" />
                        </div>
                        <span className="text-foreground/90">{benefit}</span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-6 pb-8">
                  <Button asChild size="lg" className={`w-full font-bold text-base transition-all duration-300 rounded-md ${plan.isPopular ? "bg-secondary hover:bg-secondary/90 text-secondary-foreground border-0 shadow-md relative overflow-hidden group" : "bg-transparent hover:bg-primary/5 text-foreground border border-border hover:border-primary/50"}`}>
                    <Link to={plan.link}>
                      {plan.isPopular && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />}
                      <span className="relative z-10">{plan.buttonText}</span>
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}